import axios from 'axios';
import { AuthResponse, User, Achievement, Task, UserTask, UserAchievement, Notification, DashboardData, Position } from '../types';
import { positionsAPI } from './positionsAPI';

const API_BASE_URL = 'https://vlad.voxlink.ru/api';

const api = axios.create({
  baseURL: API_BASE_URL,
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

export const authAPI = {
  login: async (username: string, password: string): Promise<AuthResponse> => {
    console.log('API login call for user:', username);
    const formData = new URLSearchParams();
    formData.append('username', username);
    formData.append('password', password);

    try {
      console.log('Sending request to:', API_BASE_URL + '/auth/login');
      const response = await api.post('/auth/login', formData, {
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
      });
      console.log('API response:', response);
      return response.data;
    } catch (error: any) {
      console.error('API login error:', error);
      console.error('Error response:', error.response);
      throw error;
    }
  },

  register: async (userData: {
    username: string;
    email: string;
    password: string;
    full_name: string;
    role: string;
    city?: string;
  }): Promise<User> => {
    const response = await api.post('/auth/register', userData);
    return response.data;
  },

  getCurrentUser: async (): Promise<User> => {
    console.log('API: Getting current user...');
    try {
      const response = await api.get('/users/me');
      console.log('API: Current user response:', response.data);
      return response.data;
    } catch (error: any) {
      console.error('API: Failed to get current user:', error);
      console.error('API: Error response:', error.response);
      throw error;
    }
  },
};

export const usersAPI = {
  getUsers: async (): Promise<User[]> => {
    const response = await api.get('/users/');
    const users = response.data;

    // Получаем все должности для отображения названий вместо ID
    try {
      const positions = await positionsAPI.getPositions();
      const positionMap = new Map(positions.map(p => [p.id, p]));

      // Добавляем информацию о должности к пользователям и обрабатываем компетенции
      return users.map((user: any) => {
        // Обрабатываем компетенции - если строка, парсим в массив
        let competencies = user.competencies;
        if (typeof competencies === 'string') {
          try {
            // Пробуем распарсить если JSON
            if (competencies.trim().startsWith('[') && competencies.trim().endsWith(']')) {
              competencies = JSON.parse(competencies);
            } else {
              // Иначе разбиваем по запятой
              competencies = competencies.split(',').map((comp: string) => comp.trim()).filter((comp: string) => comp.length > 0);
            }
          } catch (e) {
            // Если ошибка, пробуем разбить по запятой
            competencies = competencies.split(',').map((comp: string) => comp.trim()).filter((comp: string) => comp.length > 0);
          }
        }

        return {
          ...user,
          competencies,
          position: user.position_id ? positionMap.get(user.position_id) : undefined
        } as User;
      });
    } catch (error) {
      // Если не удалось получить должности, возвращаем данные без них
      console.error('Failed to load positions:', error);
      return users.map((user: any) => {
        let competencies = user.competencies;
        if (typeof competencies === 'string') {
          try {
            // Пробуем распарсить если JSON
            if (competencies.trim().startsWith('[') && competencies.trim().endsWith(']')) {
              competencies = JSON.parse(competencies);
            } else {
              // Иначе разбиваем по запятой
              competencies = competencies.split(',').map((comp: string) => comp.trim()).filter((comp: string) => comp.length > 0);
            }
          } catch (e) {
            // Если ошибка, пробуем разбить по запятой
            competencies = competencies.split(',').map((comp: string) => comp.trim()).filter((comp: string) => comp.length > 0);
          }
        }
        return { ...user, competencies } as User;
      });
    }
  },

  createUser: async (userData: {
    username: string;
    email: string;
    password: string;
    full_name: string;
    role: string;
    city?: string;
    position_id?: number;
    phone_number?: string;
    company_start_year?: number;
    competencies?: string;
  }): Promise<User> => {
    const fd = new FormData();
    fd.append('username', userData.username);
    fd.append('email', userData.email);
    fd.append('password', userData.password);
    fd.append('full_name', userData.full_name);
    fd.append('role', userData.role);
    if (userData.city) fd.append('city', userData.city);
    if (userData.position_id) fd.append('position_id', String(userData.position_id));
    if (userData.phone_number) fd.append('phone_number', userData.phone_number);
    if (userData.company_start_year) fd.append('company_start_year', String(userData.company_start_year));
    if (userData.competencies) fd.append('competencies', userData.competencies);
    const response = await api.post('/users/', fd);
    return response.data;
  },

  updateUser: async (userId: number, userData: Partial<User> & { competencies?: string }): Promise<User> => {
    const fd = new FormData();
    if (userData.full_name) fd.append('full_name', userData.full_name);
    if (userData.email) fd.append('email', userData.email);
    if (userData.city) fd.append('city', userData.city);
    if (userData.phone_number) fd.append('phone_number', userData.phone_number);
    if (userData.role) fd.append('role', userData.role);
    if (userData.position_id) fd.append('position_id', String(userData.position_id));
    if (userData.company_start_year) fd.append('company_start_year', String(userData.company_start_year));
    if (userData.competencies) fd.append('competencies', userData.competencies);
    const response = await api.put(`/users/${userId}`, fd);
    return response.data;
  },

  deleteUser: async (userId: number): Promise<void> => {
    await api.delete(`/users/${userId}`);
  },

  getColleagues: async (): Promise<User[]> => {
    const response = await api.get('/users/colleagues');
    return response.data;
  },

  uploadAvatar: async (file: File): Promise<{ avatar_path: string }> => {
    const formData = new FormData();
    formData.append('file', file);

    const response = await api.post('/users/upload-avatar', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  getCurrentUser: async (): Promise<User> => {
    const response = await api.get('/users/me');
    const userData = response.data;

    // Обрабатываем компетенции
    let competencies = userData.competencies;
    if (typeof competencies === 'string') {
      try {
        competencies = JSON.parse(competencies);
      } catch (e) {
        competencies = null;
      }
    }

    return { ...userData, competencies };
  },
};

export const achievementsAPI = {
  getPositions: async (): Promise<string[]> => {
    const response = await api.get('/achievements/positions');
    return response.data;
  },

  getAvailableAchievements: async (): Promise<Achievement[]> => {
    const response = await api.get('/achievements/available');
    return response.data;
  },

  getAchievements: async (): Promise<Achievement[]> => {
    const response = await api.get('/achievements/');
    return response.data;
  },

  createAchievement: async (achievementData: {
    title: string;
    description: string;
    required_percentage: number;
  }): Promise<Achievement> => {
    const response = await api.post('/achievements/', achievementData);
    return response.data;
  },

  updateAchievement: async (achievementId: number, achievementData: {
    title: string;
    description: string;
    required_percentage: number;
  }): Promise<Achievement> => {
    const response = await api.put(`/achievements/${achievementId}`, achievementData);
    return response.data;
  },

  deleteAchievement: async (achievementId: number): Promise<void> => {
    await api.delete(`/achievements/${achievementId}`);
  },

  getMyProgress: async (): Promise<UserAchievement[]> => {
    const response = await api.get('/achievements/my/progress');
    return response.data;
  },

  getAchievementTasks: async (achievementId: number): Promise<Task[]> => {
    const response = await api.get(`/achievements/${achievementId}/tasks`);
    return response.data;
  },

  createTask: async (achievementId: number, taskData: {
    title: string;
    description: string;
    percentage_value: number;
  }): Promise<Task> => {
    const response = await api.post(`/achievements/${achievementId}/tasks`, taskData);
    return response.data;
  },
};

export const tasksAPI = {
  getTasks: async (): Promise<Task[]> => {
    const response = await api.get('/tasks/');
    return response.data;
  },

  getMyTasks: async (): Promise<UserTask[]> => {
    const response = await api.get('/tasks/my');
    return response.data;
  },

  getAvailableTasks: async (): Promise<Task[]> => {
    const response = await api.get('/tasks/available');
    return response.data;
  },

  submitTask: async (taskId: number, submissionData: {
    submission_text?: string;
    file_path?: string;
    file_paths?: string[];
    witness_user_id?: number;
  }): Promise<UserTask> => {
    const response = await api.post(`/tasks/${taskId}/submit`, submissionData);
    return response.data;
  },

  uploadTaskFile: async (taskId: number, file: File): Promise<{ file_path: string }> => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post(`/tasks/${taskId}/upload`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  getPendingTasks: async (): Promise<UserTask[]> => {
    const response = await api.get('/tasks/pending');
    return response.data;
  },

  getWitnessPendingTasks: async (): Promise<UserTask[]> => {
    const response = await api.get('/tasks/witness/pending');
    return response.data;
  },

  witnessRespond: async (userTaskId: number, confirmed: boolean, comment?: string): Promise<UserTask> => {
    const response = await api.post(`/tasks/${userTaskId}/witness-respond`, { confirmed, comment });
    return response.data;
  },

  reviewTask: async (userTaskId: number, approved: boolean, review_comment?: string): Promise<any> => {
    const response = await api.post(`/tasks/${userTaskId}/review`, { approved, review_comment });
    return response.data;
  },

  updateTask: async (taskId: number, taskData: {
    title?: string;
    description?: string;
    achievement_id?: number;
    percentage_value?: number;
  }): Promise<Task> => {
    const response = await api.put(`/tasks/${taskId}`, taskData);
    return response.data;
  },

  deleteTask: async (taskId: number): Promise<void> => {
    await api.delete(`/tasks/${taskId}`);
  },
};

export const dashboardAPI = {
  getDashboardData: async (city?: string): Promise<DashboardData> => {
    const params = city ? { city } : {};
    const response = await api.get('/dashboard/', { params });

    // Преобразуем данные: добавляем названия должностей
    try {
      const positions = await positionsAPI.getAllPositionsForDashboard();
      const positionMap = new Map(positions.map(p => [p.id, p.title]));

      return {
        ...response.data,
        users: response.data.users.map((user: any) => ({
          ...user,
          // Временное решение: используем старое поле position если есть,
          // иначе пытаемся найти название по position_id
          position: user.position ||
                    (user.position_id && positionMap.get(user.position_id)) ||
                    undefined
        }))
      };
    } catch (error) {
      console.error('Failed to load positions for dashboard:', error);
      return response.data;
    }
  },

  getNotifications: async (): Promise<Notification[]> => {
    const response = await api.get('/dashboard/notifications');
    return response.data;
  },

  createNotification: async (notificationData: {
    title: string;
    message: string;
    notification_type: 'profile' | 'info';
    user_id?: number;
  }): Promise<Notification> => {
    const fd = new FormData();
    fd.append('title', notificationData.title);
    fd.append('message', notificationData.message);
    fd.append('notification_type', notificationData.notification_type);
    if (notificationData.user_id) fd.append('user_id', String(notificationData.user_id));
    const response = await api.post('/dashboard/notifications', fd);
    return response.data;
  },

  updateNotification: async (notificationId: number, notificationData: {
    title?: string;
    message?: string;
    notification_type?: 'profile' | 'info';
    user_id?: number;
  }): Promise<Notification> => {
    const response = await api.put(`/dashboard/notifications/${notificationId}`, notificationData);
    return response.data;
  },

  deleteNotification: async (notificationId: number): Promise<void> => {
    await api.delete(`/dashboard/notifications/${notificationId}`);
  },

  getCities: async (): Promise<string[]> => {
    const response = await api.get('/dashboard/cities');
    return response.data;
  },
};

export const fileAPI = {
  downloadFile: async (filePath: string): Promise<void> => {
    try {
      // Используем полный URL к API для скачивания файла
      const response = await api.get(`/uploads/${filePath}`, {
        responseType: 'blob',
      });

      // Создаем URL для скачивания
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;

      // Получаем имя файла из пути
      const fileName = filePath.split('/').pop() || 'file';
      link.setAttribute('download', fileName);

      // Добавляем ссылку в DOM, кликаем и удаляем
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Очищаем URL
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading file:', error);
      throw error;
    }
  },
};

export default api;